import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useResolvedParameter } from "@/features/fermentation-parameters/use-resolved-parameter";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { trpc } from "@/hooks/trpc";
import { presetForPlannedAction } from "@/features/fermentation-journal/components/timeline/planned-apply-preset";
import type {
  FermentationGroupId,
  FermentationSampleId,
  FermentationEventId,
  FermentationAdditionId,
  FermentationBrothId,
  FermentationId,
  FermentationParameterId,
  FermentationProtocolId,
  FermentationScheduledActionId,
  FermentationTimepointId,
} from "@brewbase/db/types";
import { FermentationStatusEnum } from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TimelineTable,
  TimelineInspector,
  type UnifiedTimelineEntry,
  type InspectorMode,
  type CreateEntryType,
  type SampleSaveData,
  type EventSaveData,
  type ControllerModeSaveData,
  type BrothSaveData,
  type AdditionSaveData,
  type AdditionCreateSaveData,
  type ScheduleActionSaveData,
  type ProtocolApplySaveData,
  type ProtocolApplyPreset,
  type TimelineSelection,
  type MultiTankEntryGroup,
  type ChangeBottleSaveData,
  type TimepointData,
  getTimelineSelectionId,
  type LocalProtocolApplyData,
} from "@/features/fermentation-journal";
import {
  transformApiEventsToEntries,
  mergeTimepointEntries,
} from "@/features/fermentation-journal/utils/transform-timeline-events";
import {
  calculateEftHours,
  getTimelineEntryId,
  isMultiTankGroup,
  isProximityGroupSelection,
  isScheduledEntry,
} from "@/features/fermentation-journal/types";
import { useTimelineFilters } from "@/features/fermentation-journal/hooks/use-timeline-filters";
import { FermentationTelemetryStream } from "@/features/fermentation-live/use-fermentation-telemetry-stream";
import { TimelineControls } from "@/features/fermentation-journal/components/timeline/timeline-controls";
import type { ChangeBottlePreset } from "@/features/fermentation-journal/components/timeline/change-bottle-form";
import { TimelineNowMarker } from "@/features/fermentation-journal/components/timeline/timeline-now-marker";
import {
  TimelineDataTableGroup,
  type TimelineDataTableGroupHandle,
} from "@/features/fermentation-journal/components/timeline/timeline-data-table-group";
import {
  MetricStrip,
  type JournalViewMode,
} from "@/features/fermentation-journal/components/command-center/metric-strip";
import { StripSlotsProvider } from "@/features/fermentation-journal/components/command-center/strip-slots";
import { LiveTab } from "@/features/fermentation-live/live-tab";
import { useFocusMode } from "@/hooks/use-focus-mode";
import type { ActionType, ChartOverrideSeries } from "@/features/fermentation-journal/components/command-center/types";
import { toast } from "sonner";
import { TimelineExportMenu } from "@/features/fermentation-journal/components/timeline/timeline-export-menu";
import FermentationDataUploadDialog from "@/features/data-upload/fermentation-data-upload";
import { PostApplyAutoStartDialog } from "@/features/fermentation-journal/components/timeline/post-apply-auto-start-dialog";
import type { ChangesetRescheduleData } from "@/features/fermentation-journal/components/timeline/changeset-view";
import { apiTypesForQuery } from "@/features/fermentation-journal/constants";
import { useInvalidateProtocolApply } from "@/features/fermentation-parameters/use-invalidate-protocol-apply";
import { SteadyStatePanel } from "@/features/fermentation-journal/components/results/steady-state-panel";
import { useSteadyStateAssignment } from "@/features/fermentation-journal/hooks/use-steady-state-assignment";
import { ManageKpisPanel } from "@/features/kpi-admin/components/manage-kpis-panel";
import { FormulaInspector } from "@/features/kpi-admin/components/formula-inspector";
import { useIncidentMutations } from "@/features/fermentation-journal/hooks/use-incident-mutations";
import { useEndRunStore } from "@/features/fermentation-journal/hooks/use-end-run-store";
import type { IncidentFormValues } from "@/features/fermentation-journal/components/incidents/incident-form";
import { HealthToolbar } from "@/features/tank-board/health-controls";
import { GroupPrepareBottlesDialog } from "@/features/bottle-prep/prepare-bottles-dialog";
import {
  defaultInvestigationTitle,
  incidentFromTimelineData,
} from "@/features/fermentation-journal/components/incidents/incident-labels";

/**
 * Search params: `view` picks the tab; `entry` selects a timeline entry by its
 * key (deep links from the landing page and the planning tab); `apply` opens
 * the confirmation form for a planned protocol change by its action id.
 */
export interface TimelineSearch {
  view?: JournalViewMode;
  entry?: string;
  apply?: number;
}

export const Route = createFileRoute("/fermentations/$group_id/timeline")({
  component: TimelineSection,
  validateSearch: (search: Record<string, unknown>): TimelineSearch => {
    const out: TimelineSearch = {};
    const view = search.view;
    if (view === "timeline" || view === "table" || view === "live") out.view = view;
    if (typeof search.entry === "string" && search.entry.length > 0) out.entry = search.entry;
    const apply = typeof search.apply === "string" ? Number(search.apply) : search.apply;
    if (typeof apply === "number" && Number.isInteger(apply) && apply > 0) out.apply = apply;
    return out;
  },
});


function TimelineSection() {
  const { group_id } = useParams({ from: "/fermentations/$group_id/timeline" });
  const { view: viewFromSearch, entry: entryFromSearch, apply: applyFromSearch } = Route.useSearch();
  const navigate = Route.useNavigate();
  const groupId = group_id as unknown as FermentationGroupId;

  // Filter state — default to timepoint-only since we open in table view
  const {
    filters,
    setTypeFilter,
    toggleTypeFilter,
    setTimeRange,
    setSearchQuery,
    resetFilters,
    dateRange,
  } = useTimelineFilters({ types: ["timepoint"] });

  // Fetch group data
  const { data: group, isLoading: groupLoading } =
    trpc.fermentations.groups.getWithAllDetails.useQuery(groupId);

  // Fetch unified timeline data
  const { data: timelineData, isLoading: timelineLoading } =
    trpc.fermentations.timeline.getEvents.useQuery(
      {
        groupId,
        types: filters.types ? apiTypesForQuery(filters.types) : undefined,
        fromDate: dateRange.from,
        toDate: dateRange.to,
        includeScheduled: true,
        includeKpis: true,
        sortDirection: "desc",
        limit: 200,
      },
      {
        // Keep showing previous data while fetching new filtered results
        placeholderData: keepPreviousData,
      },
    );

  // Fetch active protocols for NOW marker
  const { data: activeProtocolsData } =
    trpc.fermentations.groups.getActiveProtocols.useQuery(groupId);

  // Table edit-mode imperative handle — used to push a freshly-created
  // timepoint straight into edit mode.
  const tableRef = useRef<TimelineDataTableGroupHandle>(null);

  // State
  const [selectedEntry, setSelectedEntry] = useState<TimelineSelection | null>(
    null,
  );
  // The journal owns the full width; the sidebar is reachable as an overlay.
  useFocusMode();

  const [inspectorMode, setInspectorMode] = useState<InspectorMode>("view");
  const [createType, setCreateType] = useState<CreateEntryType | null>(null);
  // Track the scheduled action ID when recording from a scheduled item
  const [pendingScheduledActionId, setPendingScheduledActionId] =
    useState<FermentationScheduledActionId | null>(null);
  // Confirming a planned protocol change: the apply form opens pre-filled
  // with the protocol, the tanks, the proposed when and the planned items.
  const [applyPreset, setApplyPreset] = useState<ProtocolApplyPreset | null>(null);

  // View mode: timeline, table, or the live telemetry tab. The URL is the
  // source of truth so a tab is linkable; without one we fall back to the
  // last mode used for this group.
  const storedViewMode = useMemo(() => readStoredViewMode(groupId), [groupId]);
  const viewMode = viewFromSearch ?? storedViewMode;

  // Which tank the live view is showing. Lifted out of the tab so the status
  // rail beside it describes the same tank: every control on that rail names a
  // single run, and before tabs existed the rail simply hid itself for
  // multi-tank groups rather than answer the question.
  const [liveTankId, setLiveTankId] = useState<FermentationId | null>(null);

  // Portal targets for merged toolbar in table mode
  const [tableToolbarStartEl, setTableToolbarStartEl] = useState<HTMLDivElement | null>(null);
  const [tableToolbarEndEl, setTableToolbarEndEl] = useState<HTMLDivElement | null>(null);
  const [tableToolbarOverlayEl, setTableToolbarOverlayEl] = useState<HTMLDivElement | null>(null);

  // Chart override series from table column selection
  const [chartOverrideSeries, setChartOverrideSeries] = useState<ChartOverrideSeries[] | null>(null);

  // Post-apply auto-start dialog state
  const [postApplyDialog, setPostApplyDialog] = useState<{
    open: boolean;
    targetFermentationIds: FermentationId[];
    protocolId: FermentationProtocolId | null;
  }>({ open: false, targetFermentationIds: [], protocolId: null });

  // Formula inspector for group-local KPI editing
  const [editingFormulaId, setEditingFormulaId] = useState<number | null>(null);
  const [editingFormulaOwner, setEditingFormulaOwner] = useState<{ groupId: number } | null>(null);

  // Local protocol state
  const [editingLocalProtocolId, setEditingLocalProtocolId] =
    useState<FermentationProtocolId | null>(null);

  // Command center state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [prepareBottlesOpen, setPrepareBottlesOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const utils = trpc.useUtils();

  // Transform API events to internal format, then merge timepoint-linked entries
  const rawEntries = useMemo(() => {
    if (!timelineData?.events) return [];
    return transformApiEventsToEntries(timelineData.events);
  }, [timelineData?.events]);

  const timelineEntries = useMemo(() => {
    const merged = mergeTimepointEntries(rawEntries);
    // Re-sort after merge: mergeTimepointEntries appends TIMEPOINT entries
    // at the end, breaking the desc timestamp order from the API.
    merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return merged;
  }, [rawEntries]);

  // Filter by search query (client-side)
  const filteredEntries = useMemo(() => {
    if (!filters.searchQuery.trim()) return timelineEntries;

    const query = filters.searchQuery.toLowerCase();
    return timelineEntries.filter((entry) => {
      switch (entry.type) {
        case "EVENT":
        case "INCIDENT":
          return entry.data.comment?.toLowerCase().includes(query);
        case "SAMPLE":
          return entry.data.smell?.toLowerCase().includes(query);
        case "CHANGESET":
          return (
            entry.data.protocolName?.toLowerCase().includes(query) ||
            entry.data.rows.some((r) => r.parameterTypeName.toLowerCase().includes(query))
          );
        case "TIMEPOINT":
          return entry.data.sample?.smell?.toLowerCase().includes(query);
        default:
          return true;
      }
    });
  }, [timelineEntries, filters.searchQuery]);

  // The search field's placeholder and match count. The table only shows
  // timepoints, so it counts those.
  const entryCounts = useMemo(() => {
    const count = (list: typeof timelineEntries) =>
      viewMode === "table"
        ? list.filter((e) => e.type === "TIMEPOINT").length
        : list.length;
    return { shown: count(filteredEntries), total: count(timelineEntries) };
  }, [timelineEntries, filteredEntries, viewMode]);

  const isLoading = groupLoading || timelineLoading;

  // Get fermentations from group (or empty array if not loaded)
  const fermentations = group?.fermentations ?? [];

  // The bottles on every tank's lines, for the change bottle form
  const activeFeedsQueries = trpc.useQueries((t) =>
    fermentations.map((f) => t.fermentations.additions.getActiveFeeds(f.id)),
  );
  const allActiveFeeds = useMemo(
    () => activeFeedsQueries.flatMap((q) => q.data ?? []),
    [activeFeedsQueries],
  );

  // Fetch active overflow containers for each fermentation
  const overflowContainerQueries = trpc.useQueries((t) =>
    fermentations.map((f) => t.fermentations.timepoints.listOverflowContainers(f.id)),
  );
  const activeOverflowContainers = useMemo(
    () =>
      overflowContainerQueries.flatMap((q, i) => {
        const containers = q.data ?? [];
        return containers
          .filter((c) => !c.ended_at)
          .map((c) => ({
            id: c.id,
            fermentationId: fermentations[i].id,
            fermentationName: fermentations[i].name,
            containerTareG: c.container_tare_g,
            startedAt: new Date(c.started_at),
          }));
      }),
    [overflowContainerQueries, fermentations],
  );

  // Check if fermentation is running
  const isRunning = useMemo(() => {
    return fermentations.some(
      (f) => f.status === FermentationStatusEnum.RUNNING,
    );
  }, [fermentations]);

  // Get the earliest started_at from the fermentations
  const startedAt = useMemo(() => {
    const startTimes = fermentations
      .map((f) => f.started_at)
      .filter((t): t is Date => t !== null);
    if (startTimes.length === 0) return null;
    return new Date(Math.min(...startTimes.map((t) => t.getTime())));
  }, [fermentations]);

  // Get the latest ended_at — only set when ALL fermentations have ended
  const endedAt = useMemo(() => {
    if (isRunning) return null;
    const endTimes = fermentations
      .map((f) => f.ended_at)
      .filter((t): t is Date => t !== null);
    if (endTimes.length === 0) return null;
    return new Date(Math.max(...endTimes.map((t) => t.getTime())));
  }, [fermentations, isRunning]);

  // Steady state assignment (live marking) + chart member-point highlighting.
  const steadyStateAssignment = useSteadyStateAssignment(groupId);
  const {
    activeSteadyStateId,
    setActiveSteadyStateId,
  } = steadyStateAssignment;

  // The dilution rate in force on the first tank, for the feed forms' banner.
  const currentD = useResolvedParameter(groupId, fermentations[0]?.id, "dilution_rate", "1/h");

  // Get active protocol for NOW marker (use first tank's protocol for display)
  const activeProtocol = useMemo(() => {
    if (!activeProtocolsData?.fermentations?.length) return null;
    // Find first fermentation with an active protocol
    const withProtocol = activeProtocolsData.fermentations.find(
      (f) => f.activeProtocol !== null,
    );
    return withProtocol?.activeProtocol ?? null;
  }, [activeProtocolsData]);

  // Mutations
  const createSample = trpc.fermentations.samples.create.useMutation({
    onSuccess: () => {
      toast.success("Sample created");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.samples.listByGroup.invalidate(groupId);
      // Also invalidate scheduled actions in case this completed one
      utils.fermentations.scheduledActions.listPending.invalidate(groupId);
      setCreateType(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to create sample: ${error.message}`);
    },
  });

  const updateSample = trpc.fermentations.samples.update.useMutation({
    onSuccess: () => {
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.samples.listByGroup.invalidate(groupId);
    },
    onError: (error) => {
      toast.error(`Failed to update sample: ${error.message}`);
    },
  });

  const deleteSample = trpc.fermentations.samples.delete.useMutation({
    onSuccess: () => {
      toast.success("Sample deleted");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.samples.listByGroup.invalidate(groupId);
      setSelectedEntry(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to delete sample: ${error.message}`);
    },
  });

  const createEvent = trpc.fermentations.events.create.useMutation({
    onSuccess: () => {
      toast.success("Event created");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.events.listByGroup.invalidate(groupId);
      setCreateType(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to create event: ${error.message}`);
    },
  });

  const recordControllerMode =
    trpc.fermentationControl.recordControllerMode.useMutation({
      onSuccess: () => {
        toast.success("Controller change recorded");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        setCreateType(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to record the change: ${error.message}`);
      },
    });

  const updateEvent = trpc.fermentations.events.update.useMutation({
    onSuccess: () => {
      toast.success("Event updated");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.events.listByGroup.invalidate(groupId);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to update event: ${error.message}`);
    },
  });

  const deleteEvent = trpc.fermentations.events.delete.useMutation({
    onSuccess: () => {
      toast.success("Event deleted");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.events.listByGroup.invalidate(groupId);
      setSelectedEntry(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to delete event: ${error.message}`);
    },
  });

  // Broth mutations
  const createBroth = trpc.fermentations.broth.create.useMutation({
    onSuccess: () => {
      toast.success("Broth harvest started");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.broth.invalidate();
      utils.fermentations.scheduledActions.listPending.invalidate(groupId);
      setCreateType(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to start broth harvest: ${error.message}`);
    },
  });

  const updateBroth = trpc.fermentations.broth.update.useMutation({
    onSuccess: () => {
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.broth.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to update broth harvest: ${error.message}`);
    },
  });

  const deleteBroth = trpc.fermentations.broth.delete.useMutation({
    onSuccess: () => {
      toast.success("Broth harvest deleted");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.broth.invalidate();
      setSelectedEntry(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to delete broth harvest: ${error.message}`);
    },
  });

  // Addition mutations
  const updateAddition = trpc.fermentations.additions.update.useMutation({
    onSuccess: () => {
      toast.success("Addition updated");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to update addition: ${error.message}`);
    },
  });

  const deleteAddition = trpc.fermentations.additions.delete.useMutation({
    onSuccess: () => {
      toast.success("Addition deleted");
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      setSelectedEntry(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to delete addition: ${error.message}`);
    },
  });

  const createAddition =
    trpc.fermentations.additions.recordDiscrete.useMutation({
      onSuccess: () => {
        toast.success("Addition recorded");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        // Also invalidate scheduled actions in case this completed one
        utils.fermentations.scheduledActions.listPending.invalidate(groupId);
        setCreateType(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to record addition: ${error.message}`);
      },
    });

  // Feed-related mutations. Each one changes a tank's open bottle, so both
  // per-tank feed queries refetch, and a bottle put on a line is no longer ready.
  const invalidateFeeds = () => {
    fermentations.forEach((f) => {
      utils.fermentations.additions.getActiveFeeds.invalidate(f.id);
      utils.fermentations.additions.getFeedStatus.invalidate(f.id);
    });
    utils.fermentations.bottles.ready.invalidate();
  };
  const onBottleChanged = (message: string) => {
    toast.success(message);
    utils.fermentations.additions.unrecordedSwaps.invalidate();
    setChangeBottlePreset(null);
    utils.fermentations.timeline.getEvents.invalidate({ groupId });
    invalidateFeeds();
    setCreateType(null);
    setInspectorMode("view");
  };
  const attachBottle = trpc.fermentations.additions.attachBottle.useMutation({
    onSuccess: () => onBottleChanged("Bottle attached"),
    onError: (error) => {
      toast.error(`Failed to attach bottle: ${error.message}`);
    },
  });
  const swapBottle = trpc.fermentations.additions.swapBottle.useMutation({
    onSuccess: () => onBottleChanged("Bottle swapped"),
    onError: (error) => {
      toast.error(`Failed to swap bottle: ${error.message}`);
    },
  });

  // Overflow mutations
  const createOverflowContainer =
    trpc.fermentations.timepoints.createOverflowContainer.useMutation({
      onSuccess: () => {
        toast.success("Overflow container started");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        fermentations.forEach((f) =>
          utils.fermentations.timepoints.listOverflowContainers.invalidate(f.id),
        );
        setCreateType(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to create overflow container: ${error.message}`);
      },
    });

  const swapOverflowContainer =
    trpc.fermentations.timepoints.swapOverflowContainer.useMutation({
      onSuccess: () => {
        toast.success("Overflow container swapped");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        fermentations.forEach((f) =>
          utils.fermentations.timepoints.listOverflowContainers.invalidate(f.id),
        );
        setCreateType(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to swap overflow container: ${error.message}`);
      },
    });

  const cancelScheduledAction =
    trpc.fermentations.scheduledActions.cancel.useMutation({
      onSuccess: (cancelled) => {
        toast.success(
          cancelled.action_type === "PROTOCOL_APPLY"
            ? "Planned protocol change skipped"
            : "Scheduled action cancelled",
        );
        // A skipped sequence item plans the next entry, so the planned items
        // and the progression move too.
        invalidateProtocolApply([]);
        // Close the inspector after cancelling
        setSelectedEntry(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to cancel: ${error.message}`);
      },
    });

  const createScheduledAction =
    trpc.fermentations.scheduledActions.create.useMutation({
      onSuccess: () => {
        toast.success("Action scheduled");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        utils.fermentations.scheduledActions.listPending.invalidate(groupId);
        setCreateType(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to schedule action: ${error.message}`);
      },
    });

  // Every change to a run's parameters is a changeset (decision 0001).
  const invalidateProtocolApply = useInvalidateProtocolApply(groupId);
  const invalidateParameters = useCallback(() => {
    invalidateProtocolApply([]);
  }, [invalidateProtocolApply]);

  const applyProtocol = trpc.fermentations.changesets.applyProtocol.useMutation({
    onSuccess: (results) => {
      const sent = results.some((r) => r.dispatched);
      const armed = results.some((r) => !r.dispatched && r.changeset.effective_at.getTime() > Date.now());
      const confirmed = results.some((r) => r.confirmedActionId !== null);
      const planned = results.some((r) => r.nextAction !== null);
      toast.success(
        [
          sent ? "Protocol sent to the node" : armed ? "Protocol armed" : "Protocol applied",
          confirmed && planned ? "next entry planned" : null,
        ]
          .filter(Boolean)
          .join(" · "),
      );
      invalidateParameters();
      setCreateType(null);
      setInspectorMode("view");
    },
    onError: (error) => {
      toast.error(`Failed to apply protocol: ${error.message}`);
    },
  });

  const removeChangeset = trpc.fermentations.changesets.remove.useMutation({
    onSuccess: () => {
      toast.success("Changeset withdrawn");
      invalidateParameters();
      setSelectedEntry(null);
    },
    onError: (error) => {
      toast.error(`Failed to withdraw: ${error.message}`);
    },
  });

  const upsertChangeset = trpc.fermentations.changesets.upsert.useMutation({
    onSuccess: () => {
      toast.success("Changeset moved");
      invalidateParameters();
    },
    onError: (error) => {
      toast.error(`Failed to move: ${error.message}`);
    },
  });

  const deleteParameterOverride =
    trpc.fermentations.parameters.delete.useMutation({
      onSuccess: () => {
        toast.success("Parameter override undone");
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        utils.fermentations.parameters.getCurrentValuesForGroup.invalidate(
          groupId,
        );
        setSelectedEntry(null);
        setInspectorMode("view");
      },
      onError: (error) => {
        toast.error(`Failed to undo parameter override: ${error.message}`);
      },
    });

  // Handlers
  const handleSaveSample = useCallback(
    async (data: SampleSaveData) => {
      if (data.id) {
        await updateSample.mutateAsync({
          id: data.id,
          dateTime: data.dateTime,
          od600: data.od600,
          ph: data.ph,
          cdw: data.cdw,
          sampleWeightG: data.sampleWeightG,
          isFinal: data.isFinal,
          cellsLive: data.cellsLive,
          cellsTotal: data.cellsTotal,
          cellsDilution: data.cellsDilution,
          mg2MgL: data.mg2MgL,
          po4MgL: data.po4MgL,
          nh3MgL: data.nh3MgL,
          ca2MgL: data.ca2MgL,
          fe2MgL: data.fe2MgL,
          kMgL: data.kMgL,
          naGL: data.naGL,
          tspGL: data.tspGL,
          methanolVvPct: data.methanolVvPct,
          smell: data.smell,
          potentiallyContaminated: data.potentiallyContaminated,
          sampleDryG: data.sampleDryG,
        });
      } else {
        await createSample.mutateAsync({
          fermentationId: data.fermentationId,
          dateTime: data.dateTime,
          od600: data.od600,
          ph: data.ph,
          cdw: data.cdw,
          sampleWeightG: data.sampleWeightG,
          isFinal: data.isFinal,
          cellsLive: data.cellsLive,
          cellsTotal: data.cellsTotal,
          cellsDilution: data.cellsDilution,
          mg2MgL: data.mg2MgL,
          po4MgL: data.po4MgL,
          nh3MgL: data.nh3MgL,
          ca2MgL: data.ca2MgL,
          fe2MgL: data.fe2MgL,
          kMgL: data.kMgL,
          naGL: data.naGL,
          tspGL: data.tspGL,
          methanolVvPct: data.methanolVvPct,
          smell: data.smell,
          potentiallyContaminated: data.potentiallyContaminated,
          sampleDryG: data.sampleDryG,
          // Link to scheduled action - auto-completes the scheduled item
          scheduledActionId: pendingScheduledActionId,
        });
        // Clear the pending scheduled action ID after successful creation
        setPendingScheduledActionId(null);
      }
    },
    [createSample, updateSample, pendingScheduledActionId],
  );

  const handleSaveEvent = useCallback(
    async (data: EventSaveData) => {
      if (data.id) {
        await updateEvent.mutateAsync({
          id: data.id,
          comment: data.comment,
          occurredAt: data.dateTime,
        });
      } else {
        await createEvent.mutateAsync({
          fermentationId: data.fermentationId,
          comment: data.comment,
          occurredAt: data.dateTime,
        });
      }
    },
    [createEvent, updateEvent],
  );

  const handleRecordControllerMode = useCallback(
    async (data: ControllerModeSaveData) => {
      await recordControllerMode.mutateAsync({
        fermentationId: data.fermentationId,
        mode: data.mode,
        parameterTypeId: data.parameterTypeId,
        lineTypeId: data.lineTypeId,
        at: data.dateTime,
        reason: data.reason,
      });
    },
    [recordControllerMode],
  );

  // Incidents
  const incidentMutations = useIncidentMutations(groupId);
  const openEndRun = useEndRunStore((s) => s.setOpen);

  const handleSaveIncident = useCallback(
    async (
      fermentationIds: FermentationId[],
      values: IncidentFormValues,
      id?: FermentationEventId,
    ) => {
      if (id) {
        await incidentMutations.saveIncident(id, values);
      } else {
        await incidentMutations.createIncidents(fermentationIds, values);
      }
      setCreateType(null);
      setInspectorMode("view");
    },
    [incidentMutations],
  );

  const handleDeleteIncident = useCallback(
    async (id: FermentationEventId) => {
      await incidentMutations.deleteIncident(id);
      setSelectedEntry(null);
      setInspectorMode("view");
    },
    [incidentMutations],
  );

  const handleOpenInvestigation = useCallback(
    async (entry: UnifiedTimelineEntry & { type: "INCIDENT" }) => {
      const fermentation = group?.fermentations.find((f) => f.id === entry.fermentationId);
      const summary = incidentFromTimelineData(entry.data, fermentation);
      await incidentMutations.openInvestigation(
        defaultInvestigationTitle(group?.name ?? "Run", [summary]),
        [summary.id],
      );
    },
    [incidentMutations, group],
  );

  const handleDeleteSample = useCallback(
    async (id: FermentationSampleId) => {
      await deleteSample.mutateAsync(id);
    },
    [deleteSample],
  );

  const handleDeleteEvent = useCallback(
    async (id: FermentationEventId) => {
      await deleteEvent.mutateAsync(id);
    },
    [deleteEvent],
  );

  // Broth handlers
  const handleSaveBroth = useCallback(
    async (data: BrothSaveData) => {
      if (data.id) {
        await updateBroth.mutateAsync({
          id: data.id as FermentationBrothId,
          dateTime: data.dateTime,
          endedAt: data.endedAt,
          containerTareG: data.containerTareG,
          containerGrossG: data.containerGrossG,
          sampleWetG: data.sampleWetG,
          sampleDryG: data.sampleDryG,
          notes: data.notes,
        });
      } else {
        await createBroth.mutateAsync({
          fermentationId: data.fermentationId,
          dateTime: data.dateTime,
          endedAt: data.endedAt,
          containerTareG: data.containerTareG,
          containerGrossG: data.containerGrossG,
          sampleWetG: data.sampleWetG,
          sampleDryG: data.sampleDryG,
          notes: data.notes,
          scheduledActionId: pendingScheduledActionId,
        });
        setPendingScheduledActionId(null);
      }
    },
    [createBroth, updateBroth, pendingScheduledActionId],
  );

  const handleDeleteBroth = useCallback(
    async (id: FermentationBrothId) => {
      await deleteBroth.mutateAsync(id);
    },
    [deleteBroth],
  );

  // Addition handlers
  const handleSaveAddition = useCallback(
    async (data: AdditionSaveData) => {
      await updateAddition.mutateAsync({
        id: data.id,
        amountStartMassG: data.amountStartMassG,
        amountEndMassG: data.amountEndMassG,
        amountNetAddedG: data.amountNetAddedG,
        notes: data.notes,
        startedAt: data.startedAt,
        endedAt: data.endedAt,
        inventoryItemIds: data.inventoryItemIds,
      });
    },
    [updateAddition],
  );

  const handleDeleteAddition = useCallback(
    async (id: FermentationAdditionId) => {
      await deleteAddition.mutateAsync(id);
    },
    [deleteAddition],
  );

  const handleCreateAddition = useCallback(
    async (data: AdditionCreateSaveData) => {
      await createAddition.mutateAsync({
        fermentationId: data.fermentationId,
        lineTypeId: data.lineTypeId,
        inventoryItemIds: data.inventoryItemIds,
        amountG: data.amountG,
        notes: data.notes,
        timestamp: data.timestamp,
        scheduledActionId: pendingScheduledActionId,
      });
      setPendingScheduledActionId(null);
    },
    [createAddition, pendingScheduledActionId],
  );

  const handleChangeBottle = useCallback(
    async (data: ChangeBottleSaveData) => {
      if (data.replacing) {
        await swapBottle.mutateAsync({
          currentAdditionId: data.replacing.additionId,
          endMassG: data.replacing.endMassG,
          newInventoryItemIds: data.inventoryItemIds,
          decantKg: data.decantKg,
          newStartMassG: data.startMassG,
          containerTareG: data.containerTareG,
          notes: data.notes,
          swapTime: data.at,
        });
      } else {
        await attachBottle.mutateAsync({
          fermentationId: data.fermentationId,
          lineTypeId: data.lineTypeId,
          inventoryItemIds: data.inventoryItemIds,
          decantKg: data.decantKg,
          startMassG: data.startMassG,
          containerTareG: data.containerTareG,
          notes: data.notes,
          attachedAt: data.at,
        });
      }
    },
    [attachBottle, swapBottle],
  );

  const handleStartOverflow = useCallback(
    async (data: import("@/features/fermentation-journal/components/timeline/overflow-forms").StartOverflowSaveData) => {
      await createOverflowContainer.mutateAsync({
        fermentationId: data.fermentationId,
        startedAt: data.startedAt,
        containerTareG: data.containerTareG,
        notes: data.notes,
      });
    },
    [createOverflowContainer],
  );

  const handleSwapOverflow = useCallback(
    async (data: import("@/features/fermentation-journal/components/timeline/overflow-forms").SwapOverflowSaveData) => {
      await swapOverflowContainer.mutateAsync({
        currentContainerId: data.currentContainerId,
        swapTime: data.swapTime,
        newContainerTareG: data.newContainerTareG,
        notes: data.notes,
      });
    },
    [swapOverflowContainer],
  );

  const handleSelectEntry = useCallback((entry: TimelineSelection | null) => {
    setSelectedEntry(entry);
    setInspectorMode("view");
    setCreateType(null);
  }, []);


  // Change bottle opened from an unrecorded swap starts at that line and time.
  const [changeBottlePreset, setChangeBottlePreset] = useState<ChangeBottlePreset | null>(null);

  const handleCreateEntry = useCallback((type: CreateEntryType) => {
    setSelectedEntry(null);
    setCreateType(type);
    setInspectorMode("create");
  }, []);

  // Instant create handler for observation types (sample, vessel-weight, overflow, broth)
  const handleInstantCreate = useCallback(
    async (
      type: CreateEntryType,
      options: {
        fermentationIds: FermentationId[];
        dateTime: Date;
      },
    ) => {
      const { fermentationIds: targetIds, dateTime } = options;

      const entryTypeMap: Record<
        string,
        "SAMPLE" | "BROTH"
      > = {
        sample: "SAMPLE",
        broth: "BROTH",
      };
      const entryType = entryTypeMap[type];
      if (!entryType) return;

      try {
        const createdEntries: UnifiedTimelineEntry[] = [];

        for (const fermId of targetIds) {
          let result: Record<string, unknown> | undefined;
          switch (type) {
            case "timepoint":
              result = (await createSample.mutateAsync({
                fermentationId: fermId,
                dateTime,
              })) as unknown as Record<string, unknown>;
              break;
            case "broth":
              // Defaults to a single collection — a zero-length span. The form
              // switches it to a time span, which is the rarer case.
              result = (await createBroth.mutateAsync({
                fermentationId: fermId,
                dateTime,
                endedAt: dateTime,
              })) as unknown as Record<string, unknown>;
              break;
          }
          if (result) {
            createdEntries.push({
              type: entryType,
              timestamp: dateTime,
              fermentationId: fermId,
              data: { ...result, operatorName: null },
            } as UnifiedTimelineEntry);
          }
        }

        // Invalidate caches
        utils.fermentations.timeline.getEvents.invalidate({ groupId });
        utils.fermentations.samples.listByGroup.invalidate(groupId);

        setCreateType(null);

        if (createdEntries.length === 1) {
          setSelectedEntry(createdEntries[0]);
          setInspectorMode("edit");
        } else if (createdEntries.length > 1) {
          const group: MultiTankEntryGroup = {
            type: "MULTI_TANK_GROUP",
            entryType,
            timestamp: dateTime,
            entries: createdEntries as MultiTankEntryGroup["entries"],
            fermentationIds: targetIds,
          };
          setSelectedEntry(group);
          setInspectorMode("edit");
        }
      } catch (error) {
        toast.error(`Failed to create entries: ${(error as Error).message}`);
      }
    },
    [
      createSample,
      createBroth,
      utils,
      groupId,
    ],
  );

  const handleClose = useCallback(() => {
    setSelectedEntry(null);
    setCreateType(null);
    setInspectorMode("view");
    setPendingScheduledActionId(null);
    setApplyPreset(null);
    // A deep link (`entry` / `apply`) is consumed once the inspector closes.
    if (entryFromSearch !== undefined || applyFromSearch !== undefined) {
      void navigate({
        search: (prev) => ({ view: prev.view }),
        replace: true,
      });
    }
  }, [entryFromSearch, applyFromSearch, navigate]);

  const handleViewModeChange = useCallback((mode: JournalViewMode) => {
    void navigate({ search: { view: mode }, replace: true });
    writeStoredViewMode(groupId, mode);
    if (mode === "live") {
      setSelectedEntry(null);
      setCreateType(null);
      setInspectorMode("view");
      return;
    }
    if (mode === "table") {
      setSelectedEntry(null);
      setCreateType(null);
      setInspectorMode("view");
      // Default table view to show timepoint data
      setTypeFilter(["timepoint"]);
    } else {
      setChartOverrideSeries(null);
      resetFilters();
    }
  }, [groupId, navigate, setTypeFilter, resetFilters]);

  const handleChartSeriesChange = useCallback(
    (series: ChartOverrideSeries[] | null) => {
      setChartOverrideSeries(series);
    },
    [],
  );

  // Formula management handlers
  const recomputeForGroup = trpc.kpis.recomputeForGroup.useMutation({
    onSuccess: () => {
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.kpis.getResolvedFormulas.invalidate(groupId);
      utils.kpis.getKpisWithMetadata.invalidate();
      utils.kpis.hasFormulas.invalidate(groupId);
    },
  });

  const handleFormulaChanged = useCallback(() => {
    recomputeForGroup.mutate(groupId);
  }, [recomputeForGroup, groupId]);

  const handleEditFormula = useCallback((formulaId: number, owner: { groupId: number }) => {
    setEditingFormulaId(formulaId);
    setEditingFormulaOwner(owner);
  }, []);

  const handleCancelScheduledAction = useCallback(
    async (id: FermentationScheduledActionId) => {
      await cancelScheduledAction.mutateAsync(id);
    },
    [cancelScheduledAction],
  );


  // New action handlers
  const handleScheduleAction = useCallback(
    async (data: ScheduleActionSaveData) => {
      await createScheduledAction.mutateAsync({
        groupId: data.groupId,
        fermentationId: data.fermentationId,
        targetProtocolId: data.targetProtocolId,
        actionType: data.actionType,
        actionConfig: data.actionConfig,
        title: data.title,
        notes: data.notes,
        scheduledFor: data.scheduledFor,
      });
    },
    [createScheduledAction],
  );

  const handleApplyProtocol = useCallback(
    async (data: ProtocolApplySaveData) => {
      await applyProtocol.mutateAsync({
        protocolId: data.protocolId,
        fermentationIds: data.fermentationIds,
        trigger: data.trigger,
        durationH: data.durationH,
        notes: data.notes,
        overrides: data.overrides,
        supplied: data.supplied,
        scheduledActionIds: data.scheduledActionIds,
      });

      invalidateProtocolApply(data.fermentationIds);
      setApplyPreset(null);
      setPostApplyDialog({
        open: true,
        targetFermentationIds: data.fermentationIds,
        protocolId: data.protocolId,
      });
    },
    [applyProtocol, invalidateProtocolApply],
  );

  const handleDeleteParameterOverride = useCallback(
    async (id: FermentationParameterId) => {
      await deleteParameterOverride.mutateAsync(id);
    },
    [deleteParameterOverride],
  );

  const handleRemoveChangeset = useCallback(
    async (data: { fermentationId: FermentationId; name: string }) => {
      await removeChangeset.mutateAsync(data);
    },
    [removeChangeset],
  );

  const handleRescheduleChangeset = useCallback(
    async (data: ChangesetRescheduleData) => {
      await upsertChangeset.mutateAsync({
        fermentationId: data.fermentationId,
        name: data.name,
        trigger: { kind: "at", atMs: data.atMs },
        entries: data.entries,
        metadata: data.metadata,
        reason: data.reason,
      });
    },
    [upsertChangeset],
  );

  // Local protocol handlers
  const handleLocalProtocolCreated = useCallback(
    (protocolId: FermentationProtocolId) => {
      setEditingLocalProtocolId(protocolId);
      setCreateType("edit-local-protocol");
      setInspectorMode("create");
    },
    [],
  );

  const handleLocalProtocolApply = useCallback(
    async (data: LocalProtocolApplyData) => {
      await applyProtocol.mutateAsync({
        protocolId: data.protocolId,
        fermentationIds: data.fermentationIds,
        trigger: { kind: "at", atMs: data.effectiveAt.getTime() },
      });
    },
    [applyProtocol],
  );

  const handleLocalProtocolDeleted = useCallback(() => {
    setEditingLocalProtocolId(null);
    setCreateType(null);
    setInspectorMode("view");
    setSelectedEntry(null);
  }, []);

  // All fermentation IDs for instant create
  const allFermentationIds = useMemo(
    () => fermentations.map((f) => f.id),
    [fermentations],
  );

  // Deep links: `?entry=<key>` selects an entry, `?apply=<action id>` opens
  // the confirmation form for a planned protocol change. Both are derived
  // from the URL until the operator picks or closes something themselves.
  const deepLinkedEntry = useMemo(
    () =>
      entryFromSearch !== undefined
        ? (timelineEntries.find((e) => getTimelineEntryId(e) === entryFromSearch) ?? null)
        : null,
    [entryFromSearch, timelineEntries],
  );
  // A link to apply a planned change carries the item's id, and the item is
  // read on its own rather than looked for among the entries on screen. The
  // timeline is filtered -- it opens on timepoints alone -- so searching the
  // list found nothing and the link did nothing at all, silently, which is
  // the one outcome a deep link must never have.
  const { data: deepLinkedAction, isPending: deepLinkedActionPending } =
    trpc.fermentations.scheduledActions.get.useQuery(
      applyFromSearch as FermentationScheduledActionId,
      { enabled: applyFromSearch !== undefined },
    );

  const deepLinkedPreset = useMemo(
    () =>
      deepLinkedAction
        ? presetForPlannedAction(deepLinkedAction, allFermentationIds)
        : null,
    [deepLinkedAction, allFermentationIds],
  );

  // Say so rather than leaving the page looking untouched.
  useEffect(() => {
    if (applyFromSearch === undefined || deepLinkedActionPending) return;
    if (deepLinkedPreset) return;
    toast.error(
      deepLinkedAction
        ? "That planned change names no protocol; apply one by hand."
        : "That planned change is no longer there.",
    );
    void navigate({ search: (prev) => ({ ...prev, apply: undefined }), replace: true });
  }, [
    applyFromSearch,
    deepLinkedAction,
    deepLinkedActionPending,
    deepLinkedPreset,
    navigate,
  ]);
  const effectiveSelection = selectedEntry ?? (createType ? null : deepLinkedEntry);
  const effectiveApplyPreset = applyPreset ?? (createType ? null : deepLinkedPreset);
  const effectiveCreateType: CreateEntryType | null =
    createType ?? (effectiveApplyPreset ? "protocol" : null);
  const effectiveInspectorMode: InspectorMode =
    !createType && effectiveApplyPreset ? "create" : inspectorMode;

  const inspectorOpen = Boolean(effectiveSelection || effectiveCreateType);

  // Inspector panel width: wide for editing/create/timepoints, narrow for view-only
  const inspectorPanelWidth = useMemo(() => {
    if (!selectedEntry && !createType) return "320px";

    // Create and edit modes always get wide panel
    if (createType || inspectorMode === "edit") return "min(50%, 580px)";

    // Timepoint views get wide panel
    if (selectedEntry) {
      if (isProximityGroupSelection(selectedEntry)) return "320px";
      if (isMultiTankGroup(selectedEntry)) {
        return selectedEntry.entryType === "TIMEPOINT"
          ? "min(50%, 580px)"
          : "320px";
      }
      // Single entry
      return selectedEntry.type === "TIMEPOINT"
        ? "min(50%, 580px)"
        : "320px";
    }

    return "320px";
  }, [selectedEntry, createType, inspectorMode]);

  // Timepoint instant-create
  const createTimepoint = trpc.fermentations.timepoints.create.useMutation({
    onSuccess: () => {
      utils.fermentations.timeline.getEvents.invalidate({ groupId });
      utils.fermentations.samples.listByGroup.invalidate(groupId);
      utils.fermentations.scheduledActions.listPending.invalidate(groupId);
    },
  });

  const handleInstantCreateTimepoint = useCallback(
    async (targetIds: FermentationId[], dateTime: Date, scheduledActionId?: FermentationScheduledActionId | null) => {
      try {
        // Check for recent timepoints within 5 minutes
        const fiveMinAgo = new Date(dateTime.getTime() - 5 * 60 * 1000);
        const recentTimepoint = timelineEntries.find(
          (e) =>
            (e.type === "TIMEPOINT" || e.type === "SAMPLE") &&
            e.timestamp >= fiveMinAgo &&
            e.timestamp <= dateTime &&
            targetIds.includes(e.fermentationId),
        );

        if (recentTimepoint) {
          const minutesAgo = Math.round(
            (dateTime.getTime() - recentTimepoint.timestamp.getTime()) / 60000,
          );
          const timeStr = recentTimepoint.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
          const confirmed = window.confirm(
            `A timepoint was recorded ${minutesAgo} minute${minutesAgo !== 1 ? "s" : ""} ago at ${timeStr}. Create another?`,
          );
          if (!confirmed) return;
        }

        const createdEntries: UnifiedTimelineEntry[] = [];

        for (const fermId of targetIds) {
          const result = await createTimepoint.mutateAsync({
            fermentationId: fermId,
            dateTime,
            scheduledActionId,
          });
          createdEntries.push({
            type: "TIMEPOINT",
            timestamp: dateTime,
            fermentationId: fermId,
            data: {
              timepointId: result.timepoint.id,
              fermentationId: fermId,
              dateTime,
              vesselWeightG: null,
              vesselWeightSource: null,
              vesselWeightLiveG: null,
              notes: null,
              steadyStateId: null,
              sample: result.sample,
              biogenicAmines: null,
              extracellularAmines: null,
              overflowWeightLog: null,
              overflowContainer: null,
              activeOverflowContainer: null,
              activeFeeds: [],
              additionWeightLogs: [],
              effectiveParameters: [],
            } as TimepointData,
          } as UnifiedTimelineEntry);
        }

        setCreateType(null);

        // Table mode: drop the new row straight into edit mode at the first
        // editable cell so the user can start filling values immediately.
        // The imperative handle defers to the next render so the new
        // timepoint appears in the table before we try to find it.
        if (viewMode === "table") {
          if (createdEntries.length >= 1) {
            const firstId = (createdEntries[0].data as TimepointData).timepointId;
            requestAnimationFrame(() => {
              tableRef.current?.enterEditMode(firstId as FermentationTimepointId);
            });
          }
          return;
        }

        if (createdEntries.length === 1) {
          setSelectedEntry(createdEntries[0]);
          setInspectorMode("edit");
        } else if (createdEntries.length > 1) {
          const group: MultiTankEntryGroup = {
            type: "MULTI_TANK_GROUP",
            entryType: "TIMEPOINT",
            timestamp: dateTime,
            entries: createdEntries as MultiTankEntryGroup["entries"],
            fermentationIds: targetIds,
          };
          setSelectedEntry(group);
          setInspectorMode("edit");
        }
      } catch (error) {
        toast.error(`Failed to create timepoint: ${(error as Error).message}`);
      }
    },
    [createTimepoint, utils, groupId, timelineEntries, viewMode],
  );

  // Record scheduled action - maps scheduled action type to appropriate create form
  const handleRecordScheduledAction = useCallback(
    (entry: UnifiedTimelineEntry & { type: "SCHEDULED" }, mode?: "now" | "at") => {
      const actionType = entry.data.actionType;

      // Map scheduled action type to create form type
      let formType: CreateEntryType | null = null;
      switch (actionType) {
        case "SAMPLE":
        case "OVERFLOW":
        case "WEIGHT":
          // Create a timepoint, which includes all three observation types
          handleInstantCreateTimepoint(
            entry.fermentationId ? [entry.fermentationId] : allFermentationIds,
            new Date(entry.timestamp),
            entry.data.id,
          );
          return;
        case "BROTH":
          formType = "broth";
          break;
        case "ADDITION":
          formType = "addition";
          break;
        case "HARVEST":
          // Harvest is handled separately in the harvest UI
          toast.info("Harvest recording is done in the Harvest section.");
          return;
        case "PROTOCOL_APPLY": {
          // Confirming a planned protocol change: the apply form opens with
          // the protocol and tanks fixed and the planned item named per tank,
          // so applying completes it and plans the next sequence entry.
          const preset = presetForPlanned(entry, mode ?? "at", allFermentationIds);
          if (!preset) {
            toast.error("This planned change names no protocol; apply one by hand.");
            return;
          }
          setApplyPreset(preset);
          setCreateType("protocol");
          setSelectedEntry(null);
          setInspectorMode("create");
          return;
        }
        case "CUSTOM":
          // Custom reminders can't be directly recorded
          toast.info("Custom reminders are just reminders. Cancel when done.");
          return;
        default:
          toast.error(`Unknown action type: ${actionType}`);
          return;
      }

      // Store the scheduled action ID so it can be auto-completed when the entry is created
      setPendingScheduledActionId(entry.data.id);

      // Open the create form for this type
      setCreateType(formType);
      setSelectedEntry(null);
      setInspectorMode("create");
    },
    [allFermentationIds, handleInstantCreateTimepoint],
  );

  // Handle command center actions
  const handleAction = useCallback(
    (action: ActionType) => {
      switch (action) {
        case "new-timepoint":
          handleInstantCreateTimepoint(allFermentationIds, new Date());
          break;
        case "record-sample":
          handleInstantCreateTimepoint(allFermentationIds, new Date());
          break;
        case "record-broth":
          handleInstantCreate("broth", {
            fermentationIds: allFermentationIds,
            dateTime: new Date(),
          });
          break;
        case "record-addition":
          handleCreateEntry("addition");
          break;
        case "add-comment":
          handleCreateEntry("event");
          break;
        case "report-incident":
          handleCreateEntry("incident");
          break;
        case "end-fermentation":
          openEndRun(true);
          break;
        case "apply-protocol":
          handleCreateEntry("protocol");
          break;
        case "schedule-sample":
        case "schedule-protocol":
        case "schedule-harvest":
          handleCreateEntry("schedule");
          break;
        case "change-bottle":
          setChangeBottlePreset(null);
          handleCreateEntry("change-bottle");
          break;
        case "prepare-bottles":
          setPrepareBottlesOpen(true);
          break;
        case "start-overflow":
          handleCreateEntry("start-overflow");
          break;
        case "swap-overflow":
          handleCreateEntry("swap-overflow");
          break;
        case "create-local-protocol":
          handleCreateEntry("create-local-protocol");
          break;
        case "edit-local-protocol":
          handleCreateEntry("edit-local-protocol");
          break;
        case "open-command-palette":
          setCommandPaletteOpen(true);
          break;
        default:
          break;
      }
    },
    [handleCreateEntry, handleInstantCreate, handleInstantCreateTimepoint, allFermentationIds, openEndRun],
  );

  // Handle "/" keyboard shortcut for command palette
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     // Don't trigger if typing in an input
  //     if (
  //       e.target instanceof HTMLInputElement ||
  //       e.target instanceof HTMLTextAreaElement
  //     ) {
  //       return;
  //     }

  //     // "/" key opens command palette
  //     if (e.key === "/" && !commandPaletteOpen) {
  //       e.preventDefault();
  //       setCommandPaletteOpen(true);
  //     }
  //   };

  //   document.addEventListener("keydown", handleKeyDown);
  //   return () => document.removeEventListener("keydown", handleKeyDown);
  // }, [commandPaletteOpen]);

  const isSaving =
    createSample.isPending ||
    updateSample.isPending ||
    deleteSample.isPending ||
    createEvent.isPending ||
    updateEvent.isPending ||
    deleteEvent.isPending ||
    createBroth.isPending ||
    updateBroth.isPending ||
    deleteBroth.isPending ||
    updateAddition.isPending ||
    deleteAddition.isPending ||
    createAddition.isPending ||
    attachBottle.isPending ||
    swapBottle.isPending ||
    createScheduledAction.isPending ||
    cancelScheduledAction.isPending ||
    applyProtocol.isPending ||
    removeChangeset.isPending ||
    upsertChangeset.isPending ||
    deleteParameterOverride.isPending ||
    createTimepoint.isPending;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full">
        <div className="flex-1 p-4">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    );
  }

  // No group data
  if (!group) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-muted-foreground">
          Failed to load fermentation group
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* One live subscription per tank for the whole page: the run badge,
          the panel, the live tab and the parameter rows all read from the
          queries this keeps fresh. */}
      {fermentations.map((f) => (
        <FermentationTelemetryStream
          key={f.id}
          fermentationId={f.id}
        />
      ))}
      {/* Main timeline area */}
      <StripSlotsProvider>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Metric Strip */}
        <MetricStrip
          groupId={groupId}
          fermentationId={fermentations[0]?.id}
          tankName={fermentations[0]?.name}
          startedAt={startedAt}
          endedAt={endedAt}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onUploadClick={() => setUploadDialogOpen(true)}
        />

        {/* Controls bar - actions + filters in single row */}
        {/* <CommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          onAction={handleAction}
        > */}
        {viewMode !== "live" && (
        <div>
          <TimelineControls
            onAction={handleAction}
            disabled={!isRunning}
            selectedTypes={filters.types}
            onTypeFilterChange={setTypeFilter}
            onToggleType={toggleTypeFilter}
            timeRange={filters.timeRange}
            onTimeRangeChange={setTimeRange}
            searchQuery={filters.searchQuery}
            onSearchQueryChange={setSearchQuery}
            onResetFilters={resetFilters}
            shownCount={entryCounts.shown}
            totalCount={entryCounts.total}
            steadyStates={
              viewMode === "table" ? (
                <SteadyStatePanel
                  groupId={groupId}
                  selectedTankId="all"
                  fermentations={fermentations}
                  activeSteadyStateId={activeSteadyStateId}
                  onActiveChange={setActiveSteadyStateId}
                />
              ) : undefined
            }
            afterRecord={<HealthToolbar groupId={groupId} />}
            viewMode={viewMode}
            tableToolbarStartRef={setTableToolbarStartEl}
            tableToolbarEndRef={setTableToolbarEndEl}
            tableToolbarOverlayRef={setTableToolbarOverlayEl}
            trailing={
              <TimelineExportMenu
                groupId={groupId}
                groupName={group?.name ?? null}
                csvTanks={viewMode === "table" ? fermentations : undefined}
                onExportCsv={(id) => tableRef.current?.exportCsv(id)}
              />
            }
          />
        </div>
        )}
        {/* </CommandPalette> */}


        {/* Timeline content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Timeline/Table area */}
          <div
            className={cn(
              "flex-1 min-w-0",
              viewMode === "timeline"
                ? "overflow-auto"
                : "overflow-hidden flex flex-col",
              viewMode !== "table" && "border-r border-olive-6",
            )}
          >
            {viewMode === "live" ? (
              <LiveTab
                groupId={groupId}
                procedureId={group?.procedure_id ?? null}
                selectedTankId={liveTankId}
                onSelectTank={setLiveTankId}
                tanks={fermentations.map((f) => ({
                  id: f.id,
                  name: f.name ?? `F-${f.id}`,
                  colorCode: f.color_code ? String(f.color_code) : null,
                  startedAt: f.started_at ? new Date(f.started_at) : null,
                }))}
                endedAt={endedAt}
                isRunning={isRunning}
              />
            ) : viewMode === "table" ? (
              <div className="relative flex-1 flex flex-col overflow-hidden">
                <TimelineDataTableGroup
                  ref={tableRef}
                  entries={filteredEntries}
                  fermentations={fermentations}
                  startedAt={startedAt}
                  groupId={Number(groupId)}
                  isEditable={isRunning}
                  onChartSeriesChange={handleChartSeriesChange}
                  steadyStateAssignment={steadyStateAssignment}
                  onPickSwap={(preset) => {
                    setChangeBottlePreset(preset);
                    handleCreateEntry("change-bottle");
                  }}
                  formulas={(close) => (
                    <ManageKpisPanel
                      groupId={groupId}
                      onEditFormula={handleEditFormula}
                      onChanged={handleFormulaChanged}
                      onClose={close}
                    />
                  )}
                  toolbarStartPortal={tableToolbarStartEl}
                  toolbarEndPortal={tableToolbarEndEl}
                  toolbarOverlayPortal={tableToolbarOverlayEl}
                />
                {editingFormulaId != null && editingFormulaOwner && (
                  <div
                    className="absolute inset-y-0 right-0 w-[50%] z-20 border-l border-olive-6 bg-olive-2 shadow-lg overflow-auto"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setEditingFormulaId(null);
                        setEditingFormulaOwner(null);
                      }
                    }}
                    tabIndex={-1}
                  >
                    <FormulaInspector
                      formulaId={editingFormulaId}
                      owner={editingFormulaOwner}
                      onClose={() => {
                        setEditingFormulaId(null);
                        setEditingFormulaOwner(null);
                      }}
                      onChanged={handleFormulaChanged}
                    />
                  </div>
                )}
              </div>
            ) : (
              <TimelineTable
                entries={filteredEntries}
                fermentations={fermentations}
                startedAt={startedAt}
                selectedEntryId={getTimelineSelectionId(effectiveSelection)}
                onSelectEntry={handleSelectEntry}
                typeFilter={filters.types}
                nowMarker={
                  isRunning ? (
                    <TimelineNowMarker
                      startedAt={startedAt}
                      activeProtocol={activeProtocol}
                      isRunning={isRunning}
                    />
                  ) : undefined
                }
              />
            )}
          </div>

          {/* Right panel: the inspector, and nothing else. It exists only while
              an entry is selected or a create flow is open; idle it collapses
              so the timeline gets the width. Live readings are the Live view's
              job — the rail used to duplicate them and always disagreed. */}
          <div
            style={{ width: inspectorOpen ? inspectorPanelWidth : "0px" }}
            className={cn(
              "flex-shrink-0 flex flex-col overflow-hidden transition-[width] duration-200 ease-in-out",
              inspectorOpen && "border-l border-olive-6",
            )}
          >
            {inspectorOpen && (
              <div className="h-full overflow-auto bg-olive-2">
                <TimelineInspector
                    selection={effectiveSelection}
                    groupId={groupId}
                    fermentations={fermentations}
                    startedAt={startedAt}
                    isEditable={isRunning}
                    mode={effectiveInspectorMode}
                    createType={effectiveCreateType}
                    onModeChange={setInspectorMode}
                    onClose={handleClose}
                    onSaveSample={handleSaveSample}
                    onSaveEvent={handleSaveEvent}
                    onDeleteSample={handleDeleteSample}
                    onDeleteEvent={handleDeleteEvent}
                    onSaveIncident={handleSaveIncident}
                    onRecordControllerMode={handleRecordControllerMode}
                    onDeleteIncident={handleDeleteIncident}
                    onOpenInvestigation={handleOpenInvestigation}
                    onSaveBroth={handleSaveBroth}
                    onDeleteBroth={handleDeleteBroth}
                    onSaveAddition={handleSaveAddition}
                    onDeleteAddition={handleDeleteAddition}
                    onCreateAddition={handleCreateAddition}
                    onChangeBottle={handleChangeBottle}
                    changeBottlePreset={changeBottlePreset}
                    activeFeeds={allActiveFeeds}
                    onScheduleAction={handleScheduleAction}
                    onApplyProtocol={handleApplyProtocol}
                    onDeleteParameterOverride={handleDeleteParameterOverride}
                    onRemoveChangeset={handleRemoveChangeset}
                    onRescheduleChangeset={handleRescheduleChangeset}
                    onStartOverflow={handleStartOverflow}
                    onSwapOverflow={handleSwapOverflow}
                    activeOverflowContainers={activeOverflowContainers}
                    onCancelScheduledAction={handleCancelScheduledAction}
                    onRecordScheduledAction={handleRecordScheduledAction}
                    applyPreset={effectiveApplyPreset}
                    onSelectEntry={handleSelectEntry}
                    currentDilutionRate={currentD}
                    onLocalProtocolCreated={handleLocalProtocolCreated}
                    onLocalProtocolApply={handleLocalProtocolApply}
                    onLocalProtocolDeleted={handleLocalProtocolDeleted}
                    editingLocalProtocolId={editingLocalProtocolId}
                    isSaving={isSaving}
                  />
                </div>
            )}
          </div>
        </div>
      </div>
      </StripSlotsProvider>

      <FermentationDataUploadDialog
        groupId={Number(groupId)}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />

      <GroupPrepareBottlesDialog groupId={groupId} open={prepareBottlesOpen} onOpenChange={setPrepareBottlesOpen} />
      <PostApplyAutoStartDialog
        open={postApplyDialog.open}
        onOpenChange={(open) =>
          setPostApplyDialog((prev) => ({ ...prev, open }))
        }
        groupId={groupId}
        fermentations={fermentations}
        targetFermentationIds={postApplyDialog.targetFermentationIds}
        protocolId={postApplyDialog.protocolId}
      />
    </div>
  );
}

const VIEW_MODE_KEY = (groupId: number | string) =>
  `brewbase:journal-view:${groupId}`;

/**
 * The apply form's preset for confirming a planned protocol change: protocol
 * and tanks fixed, the when proposed as now or the item's due time, the
 * entry's default transition offered, and the planned item named per tank.
 * A group-wide item (no tank) maps the same id onto every tank; the server
 * expands it into one item per tank. Null when the item names no protocol.
 */
function presetForPlanned(
  entry: UnifiedTimelineEntry & { type: "SCHEDULED" },
  mode: "now" | "at",
  allFermentationIds: FermentationId[],
): ProtocolApplyPreset | null {
  const { data } = entry;
  if (data.targetProtocolId === null) return null;
  const fermentationIds = entry.fermentationId !== null ? [entry.fermentationId] : allFermentationIds;
  const transition = data.transitionDurationH !== null ? parseFloat(data.transitionDurationH) : NaN;
  const position =
    data.sequencePosition !== null
      ? `Entry ${data.sequencePosition + 1}${data.sequenceLength !== null ? ` of ${data.sequenceLength}` : ""}`
      : null;
  return {
    protocolId: data.targetProtocolId as FermentationProtocolId,
    fermentationIds,
    whenAt: mode === "at" ? new Date(entry.timestamp) : null,
    durationH: Number.isFinite(transition) && transition > 0 ? transition : null,
    scheduledActionIds: Object.fromEntries(fermentationIds.map((id) => [String(id), data.id])),
    title: "Confirm planned protocol",
    subtitle: position ? `${position} · proposed ${mode === "at" ? "at its due time" : "now"}` : undefined,
  };
}

function readStoredViewMode(groupId: number | string): JournalViewMode {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY(groupId));
    if (v === "timeline" || v === "table" || v === "live") return v;
  } catch {
    // storage unavailable
  }
  return "table";
}

function writeStoredViewMode(groupId: number | string, mode: JournalViewMode) {
  try {
    localStorage.setItem(VIEW_MODE_KEY(groupId), mode);
  } catch {
    // storage unavailable
  }
}
